import { Box, CloseButton, Dialog, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { ResponsiveModal, StatusChip } from '../../../shared'
import { CallButton } from '../../../shared/CallButton'
import { useCallBatch } from '../../../../hooks/useFollowUpCalls'
import { extractApiMessage } from '../../../../lib/apiError'
import { CALL_OUTCOME_LABELS } from '../../../../types/followUp'

interface CallBatchDetailProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  batchId: string | null
}

/** Each person on one call list: who, the call button, how many tries and the last outcome. */
export function CallBatchDetail({ isOpen, onClose, campaignId, batchId }: CallBatchDetailProps) {
  const { data, isLoading, error } = useCallBatch(campaignId, isOpen ? batchId ?? undefined : undefined)

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>{data?.batch.label ?? 'Call list'}</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            {isLoading ? (
              <VStack py={8}><Spinner size="lg" color="accent" /></VStack>
            ) : !!error || !data ? (
              <Text fontSize="sm" color="danger.fg">{extractApiMessage(error, 'Could not load this call list.')}</Text>
            ) : (
              <VStack gap={0} align="stretch" minW={0}>
                <Text fontSize="sm" color="muted" mb={2}>
                  {data.batch.called}/{data.batch.total} called
                  {data.batch.assignedCanvasserName ? ` · ${data.batch.assignedCanvasserName}` : ' · Unassigned'}
                </Text>
                {data.items.map(item => (
                  <HStack
                    key={item.itemId}
                    py={3}
                    gap={3}
                    borderTopWidth="1px"
                    borderColor="border"
                    align="center"
                    flexWrap="wrap"
                  >
                    <Box flex={1} minW="160px">
                      <Text fontSize="sm" fontWeight="medium">{item.displayName}</Text>
                      <HStack gap={2} mt={1} flexWrap="wrap">
                        {item.lastOutcome ? (
                          <StatusChip variant={item.lastOutcome === 'Talked' ? 'success' : 'neutral'}>
                            {CALL_OUTCOME_LABELS[item.lastOutcome]}
                          </StatusChip>
                        ) : (
                          <Text fontSize="xs" color="muted">Not called yet</Text>
                        )}
                        {item.attemptCount > 1 && (
                          <Text fontSize="xs" color="muted">{item.attemptCount} tries</Text>
                        )}
                      </HStack>
                    </Box>
                    <CallButton
                      phone={item.phone}
                      disabledReason={item.doNotContact ? 'Do not contact' : undefined}
                    />
                  </HStack>
                ))}
              </VStack>
            )}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
